import { constants, errorHelpers, hooks, intl, mathService, miscHelpers } from '@approvalmax/utils';
import isEqual from 'lodash/isEqual';
import { domain } from 'modules/data';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { t } from 'services/localization';
import { noValue } from 'shared/constants';
import { CreatePaymentQuotes, PaymentChargeType, PaymentType, useCreatePaymentQuotes } from 'shared/data/wallets/v1';
import { ApiAmaxPayPaymentType, CurrencyIsoCode } from 'shared/data/webApp/v2';

import { useLineItemsEnrichedByPaymentDetails } from '../useLineItemsEnrichedByPaymentDetails';
import {
    createQuoteUniqueId,
    extractLineItemIdFromQuoteUniqueId,
    getDateText,
} from './useItemsGroupedByWallet.helpers';
import { GroupedByWallet } from './useItemsGroupedByWallet.types';

export const useItemsGroupedByWallet = (companyId: string, requestId: string, items: domain.AmaxPayPaymentItem[]) => {
    // we might use `data` right from the mutation, but it will return to undefined on mutation
    // rerun, which will cause to numbers flickering
    const [paymentQuotes, setPaymentQuotes] = useState<NonNullable<CreatePaymentQuotes['response']>>();
    const [quotesLoadedOnce, setQuotesLoadedOnce] = useState(false);
    const lastPaymentQuotesParameters = useRef<CreatePaymentQuotes['body']>();

    const { lineItems: enrichedLineItems, isLoadingPaymentDetailsForBatchPayment } =
        useLineItemsEnrichedByPaymentDetails(companyId, requestId, items, {
            // cache results to 15 seconds to reduce number of requests
            // at least for some time. QA might try to create a bug on that,
            // but **be strong**, not remove this. In reality, I'm doubted, someone
            // will create, approve and pay less than in 15 seconds
            staleTime: constants.time.minutesMs * 0.25,
        });

    const {
        mutate,
        isLoading: isLoadingCreatePaymentQuotes,
        isError: isPaymentQuotesError,
    } = useCreatePaymentQuotes({
        onSuccess: (data) => {
            setPaymentQuotes(data);

            if (!quotesLoadedOnce) {
                setQuotesLoadedOnce(true);
            }
        },
    });

    const lineItemsPaymentQuotesMap = useMemo(
        () =>
            new Map(
                (paymentQuotes?.items || []).map((item) => [
                    extractLineItemIdFromQuoteUniqueId(item.uniqueId),
                    item.paymentQuote,
                ])
            ),
        [paymentQuotes?.items]
    );

    const enrichedByQuotesLineItems = useMemo(
        () =>
            enrichedLineItems.map((item) => {
                const paymentQuote = lineItemsPaymentQuotesMap.get(item.xeroBillInvoiceRequestId);

                return {
                    ...item,
                    feeAmount: paymentQuote?.clientFeeAmount,
                    paymentQuote,
                    paymentQuoteProps: {
                        formattedAmount: item.paymentDetails?.bankAccountDetails.bankAccount.currencyIsoCode
                            ? intl.formatCurrency(
                                  mathService.add(paymentQuote?.amount || 0, paymentQuote?.clientFeeAmount || 0),
                                  item.paymentDetails.bankAccountDetails.bankAccount.currencyIsoCode
                              )
                            : noValue,
                        formattedFee: item.paymentDetails?.bankAccountDetails.bankAccount.currencyIsoCode
                            ? intl.formatCurrency(
                                  paymentQuote?.clientFeeAmount,
                                  item.paymentDetails.bankAccountDetails.bankAccount.currencyIsoCode
                              )
                            : noValue,
                        paymentDate: getDateText(paymentQuote?.paymentDate) ?? noValue,
                        arriveNotice: t('takes_range_business_days_to_arrive', {
                            range:
                                item.paymentDetails?.bankAccountDetails?.paymentType === ApiAmaxPayPaymentType.priority
                                    ? '1-3'
                                    : '0-1',
                        }),
                    },
                };
            }),
        [enrichedLineItems, lineItemsPaymentQuotesMap]
    );

    const groupedByWallet = useMemo(() => {
        return enrichedByQuotesLineItems.reduce<GroupedByWallet<(typeof enrichedByQuotesLineItems)[number]>>(
            (acc, item) => {
                const walletCurrency = item.paymentDetails?.bankAccountDetails?.bankAccount?.currencyIsoCode;

                if (!walletCurrency) {
                    return acc;
                }

                if (!acc[walletCurrency]) {
                    acc[walletCurrency] = { lineItems: [], total: 0, totalFee: 0 };
                }

                acc[walletCurrency].lineItems.push(item);
                // although item.amount is number, it still can be null if we remove all digits
                // from the input on form editing, which is a bug of editing
                acc[walletCurrency].total += mathService.add(item.amount || 0, item.feeAmount || 0);
                acc[walletCurrency].totalFee += item.feeAmount || 0;

                return acc;
            },
            {} as GroupedByWallet<(typeof enrichedByQuotesLineItems)[number]>
        );
    }, [enrichedByQuotesLineItems]);

    const arrayGroupedByWallet = useMemo(
        () =>
            Object.entries(groupedByWallet).map(([currency, group]) => ({
                currency: currency as CurrencyIsoCode,
                lineItems: group.lineItems,
                total: group.total,
                totalFee: group.totalFee,
            })),
        [groupedByWallet]
    );

    const isLineItemsReadyToObtainPaymentQuotes = useMemo(() => {
        return enrichedLineItems.every((item) => {
            return (
                item.paymentDetails?.bankAccountDetails.bankAccount.currencyIsoCode &&
                item.paymentDetails?.bankAccountDetails.bankAccount.countryIsoCode &&
                item.virtualAccountDetails
            );
        });
    }, [enrichedLineItems]);

    const generateCreatePaymentQuotesPayload = useCallback(() => {
        const paymentDate = moment().seconds(0).milliseconds(0).toISOString();

        return enrichedLineItems
            .map((item) => {
                const paymentType = miscHelpers.castToEnum(
                    PaymentType,
                    item.paymentDetails?.bankAccountDetails.paymentType
                );

                if (!item.paymentDetails?.bankAccountDetails || !item.virtualAccountDetails || !paymentType) {
                    throw errorHelpers.notFoundError();
                }

                return {
                    uniqueId: createQuoteUniqueId(requestId, item.xeroBillInvoiceRequestId),
                    // although item.amount is number, it still can be null if we remove all digits
                    // from the input on form editing, which is a bug of editing
                    amount: item.amount || 0,
                    currency: item.currency,
                    accountCurrency: item.paymentDetails.bankAccountDetails.bankAccount.currencyIsoCode,
                    chargeType: miscHelpers.castToEnum(PaymentChargeType, item.virtualAccountDetails.paymentChargeType),
                    paymentType,
                    paymentDate,
                    bankCountry: item.paymentDetails.bankAccountDetails.bankAccount.countryIsoCode,
                };
            })
            .filter(Boolean) as CreatePaymentQuotes['body'];
    }, [enrichedLineItems, requestId]);

    const createPaymentQuotes = hooks.useDelayedCallback<(payload: CreatePaymentQuotes['body']) => void>(
        (payload) => {
            mutate({ params: { path: { organizationId: companyId } }, body: payload });
        },
        600,
        { mode: 'debounce' }
    );

    useEffect(() => {
        if (!isLineItemsReadyToObtainPaymentQuotes || isPaymentQuotesError) {
            return;
        }

        const payload = generateCreatePaymentQuotesPayload();

        if (payload.length && !isEqual(payload, lastPaymentQuotesParameters.current)) {
            // extra layer of request debouncing
            lastPaymentQuotesParameters.current = payload;
            createPaymentQuotes(payload);
        }
    }, [
        createPaymentQuotes,
        generateCreatePaymentQuotesPayload,
        isLineItemsReadyToObtainPaymentQuotes,
        isPaymentQuotesError,
    ]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            const payload = generateCreatePaymentQuotesPayload();

            if (payload.length) {
                mutate({ params: { path: { organizationId: companyId } }, body: payload });
            }
        }, constants.time.minutesMs * 30);

        return () => {
            clearInterval(intervalId);
        };
    }, [companyId, generateCreatePaymentQuotesPayload, mutate]);

    return {
        groupedByWallet: arrayGroupedByWallet,
        enrichedLineItems: enrichedByQuotesLineItems,
        isLoadingPaymentDetails: isLoadingPaymentDetailsForBatchPayment,
        isLoadingQuotes: isLoadingCreatePaymentQuotes && !quotesLoadedOnce,
    };
};
